// src/pages/Admin/DepensesRecettes/DepensesRecettes.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import Button from "./../../../components/Button/Button";
import "react-toastify/dist/ReactToastify.css";
import {
  getDepensesRecettesClub,
  deleteDepenseRecetteClub,
  createDepenseRecetteClub,
} from "./../../../services/depensesrecettesclubServices";
import EditDepenseRecetteModal from "./EditDepenseRecetteModal";
import AddDepenseRecetteModal from "./AddDepenseRecetteModal";
import { GoFileSymlinkFile } from "react-icons/go";
import { FaPlus, FaChartBar, FaDownload } from "react-icons/fa";
import { format } from "date-fns";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import DepensesRecettesCharts from "./DepensesRecettesCharts";
import { toAbsoluteDepensesRecettesPhotoUrl } from "../../../services/depensesrecettesclubPhotoServices";
import { getAllPlanComptables } from "../../../services/plancomptableServices";
import { getAllReglementInscription } from "../../../services/reglementinscriptionServices";
import { getSettings } from "../../../services/settingsServices";
import { getReglements as getAchatsBoutique } from "../../../services/achatsServices";

const fmtEUR = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(n)
    : "—";

const todayStr = () => new Date().toISOString().slice(0, 10);

// Helpers saison pour la boutique
const seasonBounds = (season) => {
  const [startYear, endYear] = String(season).split("-");
  return {
    start: new Date(`${startYear}-07-01T00:00:00`),
    end: new Date(`${endYear}-06-30T23:59:59`),
  };
};
const isAcompteText = (s = "") =>
  /acompte|arrhes|d[ée]p[ôo]t|deposit|advance/i.test(s);

export default function DepensesRecettes() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [q, setQ] = useState("");
  const [fSaison, setFSaison] = useState("");
  const [fCategorie, setFCategorie] = useState("");
  const [fType, setFType] = useState(""); // Dépense | Recette
  const [fPlanComptable, setFPlanComptable] = useState(""); // Compte comptable

  const [planComptables, setPlanComptables] = useState([]);

  // Modales
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Graphiques
  const [showCharts, setShowCharts] = useState(false);

  // Actions auto (states)
  const [addingCotisation, setAddingCotisation] = useState(false);
  const [addingLicences, setAddingLicences] = useState(false);
  const [addingPassSport, setAddingPassSport] = useState(false);
  const [addingReductions, setAddingReductions] = useState(false);
  const [addingBoutique, setAddingBoutique] = useState(false); // 👈 nouveau

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [res, plans] = await Promise.all([
        getDepensesRecettesClub(),
        getAllPlanComptables(),
      ]);
      setData(Array.isArray(res) ? res : []);
      setPlanComptables(Array.isArray(plans) ? plans : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la récupération des données.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const saisons = useMemo(
    () => Array.from(new Set(data.map((d) => d.saison))).filter(Boolean),
    [data]
  );
  const categories = useMemo(
    () => Array.from(new Set(data.map((d) => d.categorie))).filter(Boolean),
    [data]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((d) => {
      const matchQ =
        !query ||
        `${d.designation || ""} ${String(d.montant || "")}`
          .toLowerCase()
          .includes(query);
      const matchS = !fSaison || d.saison === fSaison;
      const matchC = !fCategorie || d.categorie === fCategorie;
      const matchT = !fType || d.type_operation === fType;
      const matchP =
        !fPlanComptable ||
        String(d.plan_comptable_id) === String(fPlanComptable);
      return matchQ && matchS && matchC && matchT && matchP;
    });
  }, [data, q, fSaison, fCategorie, fType, fPlanComptable]);

  const totals = useMemo(() => {
    const dep = filtered
      .filter((d) => d.type_operation === "Dépense")
      .reduce((s, d) => s + (d.montant || 0), 0);
    const rec = filtered
      .filter((d) => d.type_operation === "Recette")
      .reduce((s, d) => s + (d.montant || 0), 0);
    return { depense: dep, recette: rec, solde: rec - dep };
  }, [filtered]);

  // --- Colonnes d'export ---
  const EXPORT_COLUMNS = [
    "id",
    "saison",
    "date_operation",
    "designation",
    "categorie",
    "beneficiaire",
    "montant",
    "type_operation",
    "type_reglement",
    "plan_comptable_id",
    "plan_comptable_code",
    "lien_url",
    "photo_url",
  ];

  // Date Excel ou cellule vide (undefined)
  const asExcelDateOrEmpty = (d) => {
    if (!d) return undefined;
    const dt = new Date(d);
    if (isNaN(dt)) return undefined;
    if (dt.getFullYear() < 1901) return undefined;
    return dt;
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette opération ?")) return;
    try {
      await deleteDepenseRecetteClub(id);
      setData((prev) => prev.filter((d) => d.id !== id));
      toast.success("Opération supprimée.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleExportFiltered = async () => {
    try {
      // Map des lignes filtrées -> objet plat pour Excel
      const rows = (filtered || []).map((it) => {
        const plan = planComptables.find(
          (p) => String(p.id) === String(it.plan_comptable_id)
        );
        return {
          id: it?.id ?? "",
          saison: it?.saison ?? "",
          date_operation: asExcelDateOrEmpty(it?.date_operation), // Date => Excel
          designation: it?.designation ?? "",
          categorie: it?.categorie ?? "",
          beneficiaire: it?.beneficiaire ?? "",
          montant: Number.isFinite(it?.montant) ? Number(it.montant) : "", // nombre
          type_operation: it?.type_operation ?? "",
          type_reglement: it?.type_reglement ?? "",
          plan_comptable_id: it?.plan_comptable_id ?? "",
          plan_comptable_code: plan?.code ?? "",
          lien_url: it?.lien_url ?? "",
          photo_url: it?.photo_url ?? "",
        };
      });

      if (!rows.length) {
        toast.info(
          "Aucune opération à exporter (appliquez d’abord un filtre)."
        );
        return;
      }

      // Tentative XLSX d'abord
      try {
        const XLSX = await import("xlsx");
        const ws = XLSX.utils.json_to_sheet(rows, {
          header: EXPORT_COLUMNS,
          cellDates: true,
        });

        // Largeurs de colonnes lisibles
        ws["!cols"] = [
          { wch: 6 }, // id
          { wch: 10 }, // saison
          { wch: 12 }, // date_operation
          { wch: 34 }, // designation
          { wch: 16 }, // categorie
          { wch: 18 }, // beneficiaire
          { wch: 12 }, // montant
          { wch: 10 }, // type_operation
          { wch: 14 }, // type_reglement
          { wch: 8 }, // plan_comptable_id
          { wch: 12 }, // plan_comptable_code
          { wch: 28 }, // lien_url
          { wch: 28 }, // photo_url
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "depenses_recettes_filtres");
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });

        const blob = new Blob([wbout], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        a.href = url;
        a.download = `depenses_recettes_filtres_${yyyy}-${mm}-${dd}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Export XLSX généré.");
        return;
      } catch (errXlsx) {
        console.warn("xlsx indisponible, fallback CSV…", errXlsx);
      }

      // Fallback CSV (UTF-8 BOM)
      const csvHeaders = EXPORT_COLUMNS.join(",");
      const toCsvVal = (v) => {
        if (v instanceof Date && !isNaN(v)) {
          const yyyy = v.getFullYear();
          const mm = String(v.getMonth() + 1).padStart(2, "0");
          const dd = String(v.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        }
        const s = String(v ?? "");
        return `"${s.replace(/"/g, '""')}"`;
      };
      const csvBody = rows
        .map((r) => EXPORT_COLUMNS.map((k) => toCsvVal(r[k])).join(","))
        .join("\n");
      const csv = `\ufeff${csvHeaders}\n${csvBody}\n`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      a.href = url;
      a.download = `depenses_recettes_filtres_${yyyy}-${mm}-${dd}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.info("Export CSV généré (XLSX indisponible).");
    } catch (e) {
      console.error(e);
      toast.error("Échec de l’export des dépenses/recettes filtrées.");
    }
  };

  // ===== Helpers Plan Comptable =====
  const findCotisationPlan = () => {
    const pref = planComptables.find(
      (p) => /^70/i.test(p?.code || "") && /coti/i.test(p?.intitule || "")
    );
    if (pref) return pref;
    const by70 = planComptables.find((p) => /^70/i.test(p?.code || ""));
    if (by70) return by70;
    return planComptables.find((p) => p?.sens === "RECETTE") || null;
  };

  const findLicensePlans = () => {
    const recettePlan =
      planComptables.find(
        (p) =>
          /^70/i.test(p?.code || "") && /(licen|assur)/i.test(p?.intitule || "")
      ) ||
      planComptables.find((p) => /^70/i.test(p?.code || "")) ||
      planComptables.find((p) => p?.sens === "RECETTE") ||
      null;

    const depensePlan =
      planComptables.find(
        (p) =>
          /^62/i.test(p?.code || "") &&
          /(licen|assur|ffjda|france\s*judo)/i.test(p?.intitule || "")
      ) ||
      planComptables.find((p) => /^62/i.test(p?.code || "")) ||
      planComptables.find((p) => p?.sens === "DEPENSE") ||
      null;

    return { recettePlan, depensePlan };
  };

  const findPassSportPlans = () => {
    // Dépense = manque à gagner (réduction)
    const depensePlan =
      planComptables.find(
        (p) =>
          /^6[5-7]/i.test(p?.code || "") &&
          /(pass|réduc|reduc|aide)/i.test(p?.intitule || "")
      ) ||
      planComptables.find((p) => /^65|^67/.test(p?.code || "")) ||
      planComptables.find((p) => p?.sens === "DEPENSE") ||
      null;

    // Recette = remboursement de l'État (74xx Subventions)
    const recettePlan =
      planComptables.find(
        (p) =>
          /^74/i.test(p?.code || "") ||
          /(subvention|etat|pass)/i.test(p?.intitule || "")
      ) ||
      planComptables.find((p) => p?.sens === "RECETTE") ||
      null;

    return { depensePlan, recettePlan };
  };

  const findReductionsPlan = () => {
    const depensePlan =
      planComptables.find(
        (p) =>
          /^6[5-7]/i.test(p?.code || "") &&
          /(réduc|reduc|remise|famille|saison)/i.test(p?.intitule || "")
      ) ||
      planComptables.find((p) => /^65|^67/.test(p?.code || "")) ||
      planComptables.find((p) => p?.sens === "DEPENSE") ||
      null;
    return depensePlan;
  };

  const findBoutiquePlan = () => {
    // Ventes de marchandises : 707 xx
    return (
      planComptables.find(
        (p) =>
          /^707/i.test(p?.code || "") ||
          /(marchandises|vente|boutique)/i.test(p?.intitule || "")
      ) ||
      planComptables.find((p) => /^70/i.test(p?.code || "")) ||
      planComptables.find((p) => p?.sens === "RECETTE") ||
      null
    );
  };

  // ===== Actions automatiques =====

  // Cotisations (Recette)
  const onAddCotisation = async () => {
    if (addingCotisation) return;
    setAddingCotisation(true);
    try {
      let season = fSaison;
      if (!season) {
        const settings = await getSettings();
        season = settings?.[0]?.saison_active || "";
      }
      if (!season) {
        toast.error(
          "Saison introuvable. Sélectionnez une saison dans le filtre."
        );
        return;
      }

      const regs = await getAllReglementInscription();
      const totalCot = (Array.isArray(regs) ? regs : [])
        .filter(
          (r) =>
            (r?.saison || "") === season &&
            (r?.statut_reglement || "") === "Réglé"
        )
        .reduce((s, r) => s + (Number(r?.montant_cotisation) || 0), 0);

      if (!Number.isFinite(totalCot) || totalCot <= 0) {
        toast.warn(
          `Aucune cotisation "Réglé" trouvée pour la saison ${season}.`
        );
        return;
      }

      const label = `Cotisations perçues — ${season}`;
      const exists = data.some(
        (d) =>
          (d?.saison || "") === season &&
          (d?.designation || "") === label &&
          (d?.type_operation || "") === "Recette"
      );
      if (
        exists &&
        !window.confirm(
          `Une ligne "${label}" existe déjà pour cette saison. En créer une autre ?`
        )
      ) {
        return;
      }

      const plan = findCotisationPlan();
      if (!plan) {
        toast.info(
          "Aucun compte comptable RECETTE n’a été trouvé — la ligne sera créée sans plan comptable."
        );
      }

      const payload = {
        saison: season,
        date_operation: todayStr(),
        designation: label,
        categorie: "Cotisations",
        beneficiaire: "Club",
        montant: Number(totalCot.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Recette",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: plan?.id ?? null,
      };

      await createDepenseRecetteClub(payload);
      toast.success(
        `Ligne "Cotisations perçues" ajoutée (${fmtEUR(payload.montant)}).`
      );
      await fetchAll();
      if (!fSaison) setFSaison(season);
    } catch (e) {
      console.error("Ajouter Cotisation:", e);
      toast.error("Impossible d’ajouter la ligne de cotisation.");
    } finally {
      setAddingCotisation(false);
    }
  };

  // Licences (Recette + Dépense)
  const onAddLicences = async () => {
    if (addingLicences) return;
    setAddingLicences(true);
    try {
      let season = fSaison;
      if (!season) {
        const settings = await getSettings();
        season = settings?.[0]?.saison_active || "";
      }
      if (!season) {
        toast.error(
          "Saison introuvable. Sélectionnez une saison dans le filtre."
        );
        return;
      }

      const regs = await getAllReglementInscription();
      const totalLic = (Array.isArray(regs) ? regs : [])
        .filter(
          (r) =>
            (r?.saison || "") === season &&
            (r?.statut_reglement || "") === "Réglé"
        )
        .reduce((s, r) => s + (Number(r?.montant_lic_assurance) || 0), 0);

      if (!Number.isFinite(totalLic) || totalLic <= 0) {
        toast.warn(`Aucune licence "Réglé" trouvée pour la saison ${season}.`);
        return;
      }

      const labelRec = `Licences perçues — ${season}`;
      const labelDep = `Licences reversées FFJDA — ${season}`;

      const existsRec = data.some(
        (d) =>
          (d?.saison || "") === season &&
          (d?.designation || "") === labelRec &&
          (d?.type_operation || "") === "Recette"
      );
      const existsDep = data.some(
        (d) =>
          (d?.saison || "") === season &&
          (d?.designation || "") === labelDep &&
          (d?.type_operation || "") === "Dépense"
      );
      if (
        (existsRec || existsDep) &&
        !window.confirm(
          `Une ligne "Licences" existe déjà pour cette saison.\nCréer tout de même les écritures ?`
        )
      ) {
        return;
      }

      const { recettePlan, depensePlan } = findLicensePlans();
      if (!recettePlan) {
        toast.info(
          "Aucun compte RECETTE “Licences/Assurance” trouvé — la recette sera créée sans plan comptable."
        );
      }
      if (!depensePlan) {
        toast.info(
          "Aucun compte DÉPENSE “Licences/Assurance/FFJDA” trouvé — la dépense sera créée sans plan comptable."
        );
      }

      const recettePayload = {
        saison: season,
        date_operation: todayStr(),
        designation: labelRec,
        categorie: "Licences",
        beneficiaire: "Club",
        montant: Number(totalLic.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Recette",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: recettePlan?.id ?? null,
      };
      await createDepenseRecetteClub(recettePayload);

      const depensePayload = {
        saison: season,
        date_operation: todayStr(),
        designation: labelDep,
        categorie: "Licences",
        beneficiaire: "France Judo",
        montant: Number(totalLic.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Dépense",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: depensePlan?.id ?? null,
      };
      await createDepenseRecetteClub(depensePayload);

      toast.success(
        `Licences: recette (Club) et dépense (FFJDA) créées (${fmtEUR(
          totalLic
        )} chacune).`
      );
      await fetchAll();
      if (!fSaison) setFSaison(season);
    } catch (e) {
      console.error("Ajouter Licences:", e);
      toast.error(
        "Impossible d’ajouter les écritures de licences (recette + dépense)."
      );
    } finally {
      setAddingLicences(false);
    }
  };

  // Pass'Sport (Dépense + Recette)
  const onAddPassSport = async () => {
    if (addingPassSport) return;
    setAddingPassSport(true);
    try {
      let season = fSaison;
      if (!season) {
        const settings = await getSettings();
        season = settings?.[0]?.saison_active || "";
      }
      if (!season) {
        toast.error(
          "Saison introuvable. Sélectionnez une saison dans le filtre."
        );
        return;
      }

      const regs = await getAllReglementInscription();
      const totalPass = (Array.isArray(regs) ? regs : [])
        .filter(
          (r) =>
            (r?.saison || "") === season &&
            (r?.statut_reglement || "") === "Réglé"
        )
        .reduce((s, r) => {
          const v = Number(r?.pass_sport) || 0;
          return s + (v < 0 ? -v : 0);
        }, 0);

      if (!Number.isFinite(totalPass) || totalPass <= 0) {
        toast.warn(
          `Aucun Pass’Sport (négatif) trouvé pour la saison ${season}.`
        );
        return;
      }

      const labelDep = `Pass’Sport accordé — ${season}`;
      const labelRec = `Pass’Sport remboursé (État) — ${season}`;

      const existsDep = data.some(
        (d) =>
          (d?.saison || "") === season &&
          (d?.designation || "") === labelDep &&
          (d?.type_operation || "") === "Dépense"
      );
      const existsRec = data.some(
        (d) =>
          (d?.saison || "") === season &&
          (d?.designation || "") === labelRec &&
          (d?.type_operation || "") === "Recette"
      );
      if (
        (existsDep || existsRec) &&
        !window.confirm(
          `Des lignes “Pass’Sport” existent déjà pour cette saison.\nCréer tout de même les écritures ?`
        )
      ) {
        return;
      }

      const { depensePlan, recettePlan } = findPassSportPlans();
      if (!depensePlan) {
        toast.info(
          "Aucun compte DÉPENSE approprié (réduction/aide) trouvé — la dépense sera créée sans plan comptable."
        );
      }
      if (!recettePlan) {
        toast.info(
          "Aucun compte RECETTE “subventions/État” (74xx) trouvé — la recette sera créée sans plan comptable."
        );
      }

      const depensePayload = {
        saison: season,
        date_operation: todayStr(),
        designation: labelDep,
        categorie: "Pass'Sport",
        beneficiaire: "Adhérents",
        montant: Number(totalPass.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Dépense",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: depensePlan?.id ?? null,
      };
      await createDepenseRecetteClub(depensePayload);

      const recettePayload = {
        saison: season,
        date_operation: todayStr(),
        designation: labelRec,
        categorie: "Pass'Sport",
        beneficiaire: "État",
        montant: Number(totalPass.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Recette",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: recettePlan?.id ?? null,
      };
      await createDepenseRecetteClub(recettePayload);

      toast.success(
        `Pass’Sport: dépense (accordé) et recette (remboursé) créées (${fmtEUR(
          totalPass
        )} chacune).`
      );
      await fetchAll();
      if (!fSaison) setFSaison(season);
    } catch (e) {
      console.error("Ajouter Pass’Sport:", e);
      toast.error(
        "Impossible d’ajouter les écritures Pass’Sport (dépense + recette)."
      );
    } finally {
      setAddingPassSport(false);
    }
  };

  // Réductions (famille + saison) — DÉPENSE unique
  const onAddReductions = async () => {
    if (addingReductions) return;
    setAddingReductions(true);
    try {
      let season = fSaison;
      if (!season) {
        const settings = await getSettings();
        season = settings?.[0]?.saison_active || "";
      }
      if (!season) {
        toast.error(
          "Saison introuvable. Sélectionnez une saison dans le filtre."
        );
        return;
      }

      const regs = await getAllReglementInscription();
      const totalReductions = (Array.isArray(regs) ? regs : [])
        .filter(
          (r) =>
            (r?.saison || "") === season &&
            (r?.statut_reglement || "") === "Réglé"
        )
        .reduce((s, r) => {
          const rf = Math.abs(Number(r?.reduction_foyer) || 0);
          const rs = Math.abs(Number(r?.reduction_saison) || 0);
          const rc = Math.abs(Number(r?.reduc_ccas) || 0);
          return s + rf + rs + rc;
        }, 0);

      if (!Number.isFinite(totalReductions) || totalReductions <= 0) {
        toast.warn(
          `Aucune réduction (famille/saison) trouvée pour la saison ${season}.`
        );
        return;
      }

      const label = `Réductions (famille + saison) accordées — ${season}`;
      const exists = data.some(
        (d) =>
          (d?.saison || "") === season &&
          (d?.designation || "") === label &&
          (d?.type_operation || "") === "Dépense"
      );
      if (
        exists &&
        !window.confirm(
          `Une ligne “${label}” existe déjà pour cette saison. En créer une autre ?`
        )
      ) {
        return;
      }

      const plan = findReductionsPlan();
      if (!plan) {
        toast.info(
          "Aucun compte DÉPENSE approprié (réduction/remise) trouvé — la ligne sera créée sans plan comptable."
        );
      }

      const depensePayload = {
        saison: season,
        date_operation: todayStr(),
        designation: label,
        categorie: "Réductions",
        beneficiaire: "Adhérents",
        montant: Number(totalReductions.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Dépense",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: plan?.id ?? null,
      };

      await createDepenseRecetteClub(depensePayload);
      toast.success(
        `Réductions: dépense créée (${fmtEUR(depensePayload.montant)}).`
      );
      await fetchAll();
      if (!fSaison) setFSaison(season);
    } catch (e) {
      console.error("Ajouter Réductions:", e);
      toast.error("Impossible d’ajouter la ligne de réductions.");
    } finally {
      setAddingReductions(false);
    }
  };

  // 👇 Boutique — Chiffre d’affaires (Recette unique)
  const onAddBoutiqueCA = async () => {
    if (addingBoutique) return;
    setAddingBoutique(true);
    try {
      let season = fSaison;
      if (!season) {
        const settings = await getSettings();
        season = settings?.[0]?.saison_active || "";
      }
      if (!season) {
        toast.error(
          "Saison introuvable. Sélectionnez une saison dans le filtre."
        );
        return;
      }

      const { start, end } = seasonBounds(season);
      const achats = await getAchatsBoutique();
      if (!Array.isArray(achats) || achats.length === 0) {
        toast.warn("Aucun achat boutique trouvé.");
        return;
      }

      // Filtrer: lignes avec Article, dans les bornes, hors acomptes/arrhes
      const filtered = achats.filter((a) => {
        if (!a?.Article) return false;
        const d = new Date(a?.date_achat);
        if (!(d >= start && d <= end)) return false;

        const name = a.Article?.nom || "";
        const desc = a.Article?.description || "";
        const msg = a.message_achat || "";
        if (isAcompteText(name) || isAcompteText(desc) || /acompte/i.test(msg))
          return false;

        // Si vous souhaitez limiter aux vrais “boutique” :
        // if (a.type_achat && a.type_achat !== "boutique") return false;

        return true;
      });

      const totalCA = filtered.reduce(
        (s, a) => s + (Number.parseFloat(a?.prix_total) || 0),
        0
      );

      if (!Number.isFinite(totalCA) || totalCA <= 0) {
        toast.warn(
          `Aucun chiffre d’affaires boutique pour la saison ${season}.`
        );
        return;
      }

      const label = `Chiffre d’affaires boutique — ${season}`;
      const exists = data.some(
        (d) =>
          (d?.saison || "") === season &&
          (d?.designation || "") === label &&
          (d?.type_operation || "") === "Recette"
      );
      if (
        exists &&
        !window.confirm(
          `Une ligne “${label}” existe déjà pour cette saison. En créer une autre ?`
        )
      ) {
        return;
      }

      const plan = findBoutiquePlan();
      if (!plan) {
        toast.info(
          "Aucun compte RECETTE “707 ventes de marchandises” trouvé — la ligne sera créée sans plan comptable."
        );
      }

      const recettePayload = {
        saison: season,
        date_operation: todayStr(),
        designation: label,
        categorie: "Boutique",
        beneficiaire: "Club",
        montant: Number(totalCA.toFixed(2)),
        type_reglement: "Automatique",
        type_operation: "Recette",
        photo_url: "",
        lien_url: "",
        plan_comptable_id: plan?.id ?? null,
      };

      await createDepenseRecetteClub(recettePayload);
      toast.success(
        `Boutique: écriture de recette créée (${fmtEUR(
          recettePayload.montant
        )}).`
      );
      await fetchAll();
      if (!fSaison) setFSaison(season);
    } catch (e) {
      console.error("Ajouter CA Boutique:", e);
      toast.error(
        "Impossible d’ajouter l’écriture de chiffre d’affaires boutique."
      );
    } finally {
      setAddingBoutique(false);
    }
  };

  // Tooltip Plan Comptable
  const getPlanLabel = (id) => {
    const p = planComptables.find((x) => String(x.id) === String(id));
    return p ? `${p.code} — ${p.intitule}` : "";
  };

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Dépenses & Recettes"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + résumé + actions générales */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Visualisez vos dépenses et recettes du club
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Utilisez les filtres ci-dessous pour affiner l’affichage.
              </p>

              {/* Résumé rapide */}
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto bg-slate-200 p-4 rounded-xl bg-white/80 dark:bg-slate-800/80">
                <StatPill
                  label="Total Dépenses"
                  value={fmtEUR(totals.depense)}
                  tone="red"
                />
                <StatPill
                  label="Total Recettes"
                  value={fmtEUR(totals.recette)}
                  tone="green"
                />
                <StatPill
                  label="Solde"
                  value={fmtEUR(totals.solde)}
                  tone={totals.solde >= 0 ? "green" : "red"}
                />
              </div>

              {/* Actions générales */}
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <Button
                  text="Ajouter une opération"
                  icon={FaPlus}
                  onClick={() => setShowAdd(true)}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md gap-1"
                />
                <Button
                  text={
                    showCharts
                      ? "Masquer les graphiques"
                      : "Afficher les graphiques"
                  }
                  icon={FaChartBar}
                  onClick={() => setShowCharts((v) => !v)}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md gap-1"
                />
                <Button
                  text="Exporter les lignes filtrées"
                  icon={FaDownload}
                  onClick={handleExportFiltered}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md gap-1"
                />
              </div>
            </div>
          </div>

          {/* Filtres */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <input
                type="text"
                placeholder="Rechercher (désignation, montant)…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
              <select
                value={fSaison}
                onChange={(e) => setFSaison(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Toutes les saisons</option>
                {saisons.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <select
                value={fCategorie}
                onChange={(e) => setFCategorie(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Toutes les catégories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                value={fType}
                onChange={(e) => setFType(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Tous types</option>
                <option value="Dépense">Dépense</option>
                <option value="Recette">Recette</option>
              </select>
              {/* Filtre Plan Comptable */}
              <select
                value={fPlanComptable}
                onChange={(e) => setFPlanComptable(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Tous comptes</option>
                {planComptables.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} — {p.intitule}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ✅ Bloc séparé — Actions automatiques */}
          <div className="px-5 pb-4 sm:px-8">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 sm:p-5">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Actions automatiques (à partir des règlements “Réglé” / ventes
                boutique)
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                Ces actions agrègent les montants de la saison sélectionnée (ou
                de la saison active si aucun filtre n’est choisi), puis créent
                les écritures correspondantes dans votre journal
                Dépenses/Recettes.
              </p>

              {/* Cotisations perçues (Recette) */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr,auto] items-center gap-3 rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 p-3">
                <div className="text-sm">
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    Cotisations perçues —{" "}
                    <span className="text-emerald-700">Recette</span>
                  </div>
                  <div className="text-slate-600 dark:text-slate-400">
                    Somme des{" "}
                    <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">
                      montant_cotisation
                    </code>{" "}
                    au statut <em>Réglé</em>. Bénéficiaire :{" "}
                    <strong>Club</strong>.
                  </div>
                </div>
                <Button
                  text={addingCotisation ? "Calcul…" : "Ajouter Cotisation"}
                  icon={FaPlus}
                  onClick={onAddCotisation}
                  disabled={addingCotisation}
                  className="!rounded-xl"
                />
              </div>

              {/* Licences (Recette + Dépense) */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr,auto] items-center gap-3 rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 p-3">
                <div className="text-sm">
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    Licences FFJDA —{" "}
                    <span className="text-emerald-700">Recette (Club)</span>{" "}
                    &nbsp;+&nbsp;
                    <span className="text-rose-700">Dépense (FFJDA)</span>
                  </div>
                  <div className="text-slate-600 dark:text-slate-400">
                    Somme des{" "}
                    <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">
                      montant_lic_assurance
                    </code>{" "}
                    au statut <em>Réglé</em>. Crée “Licences perçues” (bénéf.
                    Club) et “Licences reversées FFJDA”.
                  </div>
                </div>
                <Button
                  text={addingLicences ? "Calcul…" : "Ajouter Licences"}
                  icon={FaPlus}
                  onClick={onAddLicences}
                  disabled={addingLicences}
                  className="!rounded-xl"
                />
              </div>

              {/* Pass'Sport (Dépense + Recette) */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr,auto] items-center gap-3 rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 p-3">
                <div className="text-sm">
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    Pass’Sport —{" "}
                    <span className="text-rose-700">Dépense (accordé)</span>{" "}
                    &nbsp;+&nbsp;
                    <span className="text-emerald-700">
                      Recette (remboursé par l’État)
                    </span>
                  </div>
                  <div className="text-slate-600 dark:text-slate-400">
                    Additionne les valeurs <em>négatives</em> de{" "}
                    <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">
                      pass_sport
                    </code>{" "}
                    au statut <em>Réglé</em> (valeur absolue). Crée deux
                    écritures : dépense “Pass’Sport accordé” (bénéf. Adhérents)
                    et recette “Pass’Sport remboursé (État)”.
                  </div>
                </div>
                <Button
                  text={addingPassSport ? "Calcul…" : "Ajouter Pass’Sport"}
                  icon={FaPlus}
                  onClick={onAddPassSport}
                  disabled={addingPassSport}
                  className="!rounded-xl"
                />
              </div>

              {/* Réductions (famille + saison) — DÉPENSE */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr,auto] items-center gap-3 rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 p-3">
                <div className="text-sm">
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    Réductions (famille + saison) accordées —{" "}
                    <span className="text-rose-700">Dépense</span>
                  </div>
                  <div className="text-slate-600 dark:text-slate-400">
                    Somme de{" "}
                    <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">
                      reduction_foyer
                    </code>{" "}
                    +{" "}
                    <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">
                      reduction_saison
                    </code>{" "}
                    pour les règlements <em>Réglé</em> (valeurs absolues).
                    Bénéficiaire : <strong>Adhérents</strong>. Crée une seule
                    écriture.
                  </div>
                </div>
                <Button
                  text={addingReductions ? "Calcul…" : "Ajouter Réductions"}
                  icon={FaPlus}
                  onClick={onAddReductions}
                  disabled={addingReductions}
                  className="!rounded-xl"
                />
              </div>

              {/* 👇 Boutique — Chiffre d’affaires (Recette) */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr,auto] items-center gap-3 rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 p-3">
                <div className="text-sm">
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    Chiffre d’affaires boutique —{" "}
                    <span className="text-emerald-700">Recette</span>
                  </div>
                  <div className="text-slate-600 dark:text-slate-400">
                    Somme des ventes d’articles (kimonos, passeport sportif,
                    stages, …)
                    <strong> hors acomptes</strong>, sur la saison. Bénéficiaire
                    : <strong>Club</strong>. Tente d’affecter le compte{" "}
                    <em>707</em>.
                  </div>
                </div>
                <Button
                  text={addingBoutique ? "Calcul…" : "Ajouter CA Boutique"}
                  icon={FaPlus}
                  onClick={onAddBoutiqueCA}
                  disabled={addingBoutique}
                  className="!rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* GRAPHIQUES */}
          {showCharts && !loading && (
            <div className="px-5 pb-2 sm:px-8">
              {filtered.length === 0 ? (
                <div className="text-center py-10">
                  <h3 className="text-base font-semibold text-slate-800 dark:text-slate-200">
                    Aucune donnée à afficher
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
                    Ajoutez des opérations ou modifiez vos filtres.
                  </p>
                </div>
              ) : (
                <DepensesRecettesCharts rows={filtered} saison={fSaison} />
              )}
            </div>
          )}

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {/* Loading skeleton */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucune opération
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajustez vos filtres ou ajoutez une première opération.
                </p>
              </div>
            )}

            {/* TABLE DESKTOP */}
            {!loading && filtered.length > 0 && (
              <div className="hidden md:block">
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                        {[
                          "Date",
                          "Photo",
                          "Désignation",
                          "Montant",
                          "Bénéficiaire",
                          "Catégorie",
                          "Type",
                          "Règlement",
                          "Compte",
                          "Lien",
                          "Actions",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                      {filtered.map((it) => (
                        <tr
                          key={it.id}
                          className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                        >
                          <td className="px-4 py-3">
                            {it.date_operation
                              ? format(new Date(it.date_operation), "dd/MM/yy")
                              : "—"}
                          </td>
                          <td className="px-4 py-3">
                            {it.photo_url ? (
                              <img
                                src={toAbsoluteDepensesRecettesPhotoUrl(
                                  it.photo_url
                                )}
                                alt={it.designation || "Pièce"}
                                className="h-12 w-12 object-cover rounded-lg ring-2 ring-white dark:ring-slate-800"
                              />
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                            {it.designation || "—"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-sm font-semibold ${
                                it.type_operation === "Dépense"
                                  ? "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200"
                                  : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                              }`}
                            >
                              {fmtEUR(it.montant || 0)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {it.beneficiaire || "—"}
                          </td>
                          <td className="px-4 py-3">{it.categorie || "—"}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold text-white ${
                                it.type_operation === "Dépense"
                                  ? "bg-rose-600"
                                  : "bg-emerald-600"
                              }`}
                            >
                              {it.type_operation || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {it.type_reglement || "—"}
                          </td>

                          {/* Colonne Plan Comptable */}
                          <td className="px-4 py-3">
                            {it.plan_comptable_id ? (
                              <span
                                title={getPlanLabel(it.plan_comptable_id)}
                                className="cursor-help"
                              >
                                {planComptables.find(
                                  (p) =>
                                    String(p.id) ===
                                    String(it.plan_comptable_id)
                                )?.code || "—"}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>

                          <td className="px-4 py-3">
                            {it.lien_url ? (
                              <a
                                href={it.lien_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex"
                                title="Ouvrir le justificatif"
                              >
                                <GoFileSymlinkFile className="h-6 w-6 text-blue-500" />
                              </a>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <ButtonEdit
                                onClick={() => {
                                  setCurrentItem(it);
                                  setShowEdit(true);
                                }}
                              />
                              <ButtonDelete onClick={() => onDelete(it.id)} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* CARTES MOBILE */}
            {!loading && filtered.length > 0 && (
              <div className="md:hidden space-y-3">
                {filtered.map((it) => (
                  <div
                    key={`card-${it.id}`}
                    className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0">
                        {it.photo_url ? (
                          <img
                            src={toAbsoluteDepensesRecettesPhotoUrl(
                              it.photo_url
                            )}
                            alt={it.designation || "Pièce"}
                            className="h-16 w-16 rounded-lg object-cover ring-2 ring-white dark:ring-slate-800"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-lg bg-slate-200 dark:bg-slate-700" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {it.designation || "—"}
                          </h3>
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[12px] font-semibold ${
                              it.type_operation === "Dépense"
                                ? "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200"
                                : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                            }`}
                          >
                            {fmtEUR(it.montant || 0)}
                          </span>
                        </div>

                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {it.date_operation
                            ? format(new Date(it.date_operation), "dd/MM/yy")
                            : "—"}{" "}
                          • {it.categorie || "—"} • {it.type_reglement || "—"}
                        </p>

                        <div className="mt-2 flex items-center gap-1">
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${
                              it.type_operation === "Dépense"
                                ? "bg-rose-600"
                                : "bg-emerald-600"
                            }`}
                          >
                            {it.type_operation || "—"}
                          </span>
                          {it.lien_url && (
                            <a
                              href={it.lien_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] ring-1 ring-slate-300 dark:ring-slate-700"
                            >
                              Justificatif ↗
                            </a>
                          )}
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                          <ButtonEdit
                            onClick={() => {
                              setCurrentItem(it);
                              setShowEdit(true);
                            }}
                          />
                          <ButtonDelete onClick={() => onDelete(it.id)} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showEdit && (
        <EditDepenseRecetteModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          depenseRecette={currentItem}
          refreshList={fetchAll}
        />
      )}
      {showAdd && (
        <AddDepenseRecetteModal
          isOpen={showAdd}
          onClose={() => setShowAdd(false)}
          refreshList={fetchAll}
        />
      )}
    </section>
  );
}

/** Petit composant pour les 3 stats du bandeau */
function StatPill({ label, value, tone = "slate" }) {
  const tones = {
    red: "bg-rose-700/80 ring-rose-200/30 dark:ring-rose-800/50",
    green: "bg-emerald-700/80 ring-emerald-200/30 dark:ring-emerald-800/50",
    slate: "bg-slate-700/80 ring-slate-200/30 dark:ring-slate-800/50",
  };
  return (
    <div
      className={`rounded-xl px-4 py-3 text-center ring-1 ${
        tones[tone] || tones.slate
      }`}
    >
      <p className="text-xs opacity-90">{label}</p>
      <p className="mt-1 text-base font-semibold">{value}</p>
    </div>
  );
}
